import * as THREE from 'three'
import BaseTool from '@glass-designer/tools/baseTool'
import BaseLineTool from '@glass-designer/tools/action/baseLineTool'
import EditingLine from '@glass-designer/objects/editingLine'
import DashedLineObject from '@glass-designer/objects/dashedLineObject'

import Config from '@glass-designer/config'
import MathsUtils from '@/utils/mathsUtils'

import GlassDesignerTypes from '@glass-designer/types'


// Cut tool
// Tool to cut a guilotine line across the shape
export default class CutTool extends BaseLineTool {
  constructor(editor) {
    super(editor)

    this.type = GlassDesignerTypes.ToolTypes.cutTool
    this.flipPlaneDirection = false

    this.traceOptions.ignoreSplitLines = true
    this.traceOptions.ignoreDatumLines = true

    // Cast out lines used to show cast lines for straight edge cuts
    this.castStraightCutLine = new DashedLineObject(this.editor, {
      colour: this.editor.getTheme().editingDashLineColour,
      dashSize: 12,
      gapSize: 8,
      dashScale: 1.5
    })
    this.castStraightCutLine.setVisible(false)
    this.addChildObject(this.castStraightCutLine)
  }

  // Tool Active
  // Called when the tool is active
  onToolActive(options = {}) {
    super.onToolActive(options)

    // Start point selections
    this.editor.clearSelection()
    this.resetCuttingLine()    

    this.displayToolSettingsPopover()
  }

  // Tool Inactive
  // Called when the tool is no longer active
  onToolInactive() {
    super.onToolInactive()

    this.castStraightCutLine.setVisible(false)
  }

  // onBeforeRender
  // Called prior to rendering of the object
  onBeforeRender() {
    super.onBeforeRender()

    this.editingLine.setVisible(this.lineDetails.startPointSelected && this.lineDetails.endPointSelected)
  }

  // Reset the cutting line process
  resetCuttingLine() {
    this.editor.component.showToolHelpBox('fa-scalpel-path', 'Click and place the start of the cutting line')

    this.flipPlaneDirection = false
    this.resetLineDetails()

    this.castStraightCutLine.setVisible(false)

    this.editor.flagRenderRequired()
  }

  // Display the tool settings popover for this tool
  displayToolSettingsPopover() {
    var lineLength = 0
    var displayOptions = false

    if(this.lineDetails.startPointSelected && this.lineDetails.endPointSelected) {
      displayOptions = true

      lineLength = MathsUtils.roundNumber(this.lineDetails.start.distanceTo(this.editingLine.endPoint), 0)
    }

    this.editor.component.displayCutToolPopover({
      cutData: {
        lineLength: lineLength,
        cutAvailable: displayOptions
      },
      context: this,
      cancelCallback: (ctx, userCancelled) => {
        if(userCancelled)
          ctx.editor.cancelActiveTool()
      },
      actionCallback: (ctx, action) => {
        switch(action) {
          case 'cutInside':
            ctx.applyCutToShape(true)
            break
          case 'cutOutside':
            ctx.applyCutToShape(false)
            break
          case 'clipInside':
            ctx.applyClipToShape(true)
            break
          case 'clipOutside':
            ctx.applyClipToShape(false)
            break
          case 'seperate':
            ctx.applySeperateToShape()
            break
          case 'slice':
            ctx.applySliceToShape()
            break
        }
      }
    })
  }

  // onMouseDownEvent
  onMouseDownEvent(ev, mouseData) {
    super.onMouseDownEvent(ev, mouseData)
  }

  // onMouseMoveEvent
  onMouseMoveEvent(ev, mouseData) {
    super.onMouseMoveEvent(ev, mouseData)

    if(this.lineDetails.settingEndPoint && ev.button == Config.Mouse.buttonSelect) {

      this._updateCuttingLineEndpoint(mouseData)
    }
  }

  // onMouseUpEvent
  onMouseUpEvent(ev, mouseData) {
    super.onMouseUpEvent(ev, mouseData)

    if(ev.button == Config.Mouse.buttonSelect) {
      if(this._updateCuttingLinePoints(mouseData)) {
        this.editor.flagRenderRequired()
        return true
      }
    }

    return false
  }

  onMouseWheelEvent(ev, mouseData) {
    super.onMouseWheelEvent(ev, mouseData)
  }

  onTouchStartEvent(ev, touchData) {
    super.onTouchStartEvent(ev, touchData)
  }
  onTouchEndEvent(ev, touchData) {
    super.onTouchEndEvent(ev, touchData)

    if(this._updateCuttingLinePoints(touchData)) {
      this.editor.flagRenderRequired()
      return true
    }

    return false
  }
  onTouchCancelEvent(ev, touchData) {
    super.onTouchCancelEvent(ev, touchData)
  }
  onTouchMoveEvent(ev, touchData) {
    super.onTouchMoveEvent(ev, touchData)

    if(this.lineDetails.settingEndPoint) {

      this._updateCuttingLineEndpoint(touchData)
    }
  }


  /**
   * Update the cutting line endpoint
   * Done during mouse move / touch move
   */
  _updateCuttingLineEndpoint(mouseTouchData) {
    if(!this.selectedShape)
      return
      
    this.lineDetails.endPointSelected = true
    
    var nearestPoint = this.snapToNearestShapePoint(this.selectedShape, new THREE.Vector2(mouseTouchData.position.x, mouseTouchData.position.y)) 
    nearestPoint = this.snapLineToAngle(nearestPoint, true, new THREE.Vector2(mouseTouchData.position.x, mouseTouchData.position.y))

    this.lineDetails.end = nearestPoint

    // Update editing line
    this.editingLine.setStartPoint(this.lineDetails.start.clone())
    this.editingLine.setEndPoint(this.lineDetails.end.clone())    
    this.editingLine.startAngleFixed = this.lineDetails.startAngleFixed
    this.editingLine.endAngleFixed = this.lineDetails.endAngleFixed

    this.traceEditingLine()
    this.onEditingLineUpdated()

    this.editor.flagRenderRequired()
  }

  /**
   * Update the cutting line start/end points from mouse/touch event
   * Returns true if the cutting line was updated & the mouse/touch event was processed
   */
  _updateCuttingLinePoints(mouseTouchData) {
    if(!this.lineDetails.startPointSelected) {
      if(mouseTouchData.isSnapGridEnabled)
        this.lineDetails.start.set(mouseTouchData.snappedPosition.x, mouseTouchData.snappedPosition.y)
      else
        this.lineDetails.start.set(mouseTouchData.position.x, mouseTouchData.position.y)

      // Get shape closest to the cutting line
      this.selectedShape = this.editor.findClosestGlassShape(this.lineDetails.start, { ignoreStockLinked: true, ignoreSegmentListShapeUneditable: true })
      if(!this.selectedShape)
        return false
        
      this.selectedShapeBounds = this.selectedShape.calculateSegmentBounds()
      this.selectedShapeBounds.getCenter(this.selectedShapeCenter)

      this.castStraightCutLine.setVisible(true)

      this.lineDetails.startPointSelected = true
      this.lineDetails.settingEndPoint = true

      // Attempt to snap to the nearest vertex on the shape
      let nearestPoint = this.snapToNearestShapePoint(this.selectedShape, new THREE.Vector2(mouseTouchData.position.x, mouseTouchData.position.y))
      this.lineDetails.start = nearestPoint

      // Disable the editing line from touch/mouse events to stop it receiving clicks
      this.editingLine.setEnabled(false)

      this.editor.component.showToolHelpBox('fa-scalpel-path', 'Click and place the end of the cutting line')
      return true
    }

    if(this.lineDetails.settingEndPoint) {
      this._updateCuttingLineEndpoint(mouseTouchData)

      this.editor.component.showToolHelpBox('fa-scalpel-path', 'Modify the cutting line as required')
      this.lineDetails.settingEndPoint = false

      // Enable the editing line now it's placed
      this.editingLine.setEnabled(true)

      this.castStraightCutLine.setVisible(true)

      // Set the cutting line to match the final traced values
      this.updateCuttingLineToTracedLine()
      this.updateEditingLineLinkedObjects()

      // Setup dimensions for the selected shape & editing line
      this.editor.onToolEditingUpdated(this.selectedShape, [this.editingLine])

      // Update settings popover
      this.displayToolSettingsPopover()

      return true
    }
  }

  /**
   * Receive an object event from objects we're listening too
   */
  onReceiveObjectEvent(object, eventType, data) {
    if(eventType == GlassDesignerTypes.ObjectEventTypes.objectMoved) {
      if(object == this.editingLine.startHandle) {

        this.lineDetails.start.set(this.editingLine.startHandle.position.x, this.editingLine.startHandle.position.y, this.lineDetails.start.z)

        // Snap cut start point to nearest part of the shape
        let closestPoint = this.snapToNearestShapePoint(this.selectedShape, this.editingLine.startHandle.position)
        closestPoint = this.snapLineToAngle(closestPoint, false, this.editingLine.startHandle.position)

        this.lineDetails.start.set(closestPoint.x, closestPoint.y, this.lineDetails.end.z)

        this.editingLine.setStartPoint(this.lineDetails.start)
        this.traceEditingLine({ updateStartPoint: false, updateEndPoint: false })
        this.updateEditingLineLinkedObjects()
        this.onEditingLineUpdated()

        // Setup dimensions for the selected shape & editing line
        this.editor.onToolEditingUpdated(this.selectedShape, [this.editingLine])
        
        this.editor.flagRenderRequired()
      }
      else if(object == this.editingLine.endHandle) {

        this.lineDetails.end.set(this.editingLine.endHandle.position.x, this.editingLine.endHandle.position.y, this.lineDetails.end.z)

        // Snap cut end point to nearest part of the shape
        let closestPoint = this.snapToNearestShapePoint(this.selectedShape, this.editingLine.endHandle.position)
        closestPoint = this.snapLineToAngle(closestPoint, true, this.editingLine.endHandle.position)

        this.lineDetails.end.set(closestPoint.x, closestPoint.y, this.lineDetails.end.z)

        this.editingLine.setEndPoint(this.lineDetails.end)
        this.traceEditingLine({ updateStartPoint: false, updateEndPoint: false })
        this.updateEditingLineLinkedObjects()
        this.onEditingLineUpdated()
        
        // Setup dimensions for the selected shape & editing line
        this.editor.onToolEditingUpdated(this.selectedShape, [this.editingLine])

        this.editor.flagRenderRequired()
      }
      else if(object == this.editingLine) {
        this.lineDetails.start.set(this.lineDetails.start.x + data.moveOffset.x, this.lineDetails.start.y + data.moveOffset.y, this.lineDetails.start.z)
        this.lineDetails.end.set(this.lineDetails.end.x + data.moveOffset.x, this.lineDetails.end.y + data.moveOffset.y, this.lineDetails.end.z)

        // Run trace when moving the editing line
        this.traceEditingLine({
          updateStartPoint: true,
          updateEndPoint: true,
          referencePoint: data.grabPoint
        })

        this.updateEditingLineLinkedObjects()

        // Setup dimensions for the selected shape & editing line
        this.editor.onToolEditingUpdated(this.selectedShape, [this.editingLine])
        this.onEditingLineUpdated()

        this.editor.flagRenderRequired()
      }

    }

    if(eventType == GlassDesignerTypes.ObjectEventTypes.objectReleased) {

      // Set the cut line to match the final traced values
      this.updateCuttingLineToTracedLine()

      // Update dimensions after moving objects
      this.editor.onToolEditingUpdated(this.selectedShape, [this.editingLine])

      // Update info on popover
      this.displayToolSettingsPopover()
    }
  }


  /**
   * Update the cutting line settings to match the final raytraced points
   */
  updateCuttingLineToTracedLine() {
    this.lineDetails.start = this.editingLine.startPoint.clone()
    this.lineDetails.end = this.editingLine.endPoint.clone()
  }

  /**
   * Apply the cut to the shape, keeping the left / right side of the cutting line
   * Applies as a cutting plane, cutting the entire plane. Simulations a complete straight line cut
   */
  applyCutToShape(isKeepInside) {
    let lineDir = new THREE.Vector2(this.lineDetails.end.x - this.lineDetails.start.x, this.lineDetails.end.y - this.lineDetails.start.y)
    lineDir.normalize()
    var lineExtendDir = new THREE.Vector2(-lineDir.y, lineDir.x)

    // Flip based on what side we keep/lose
    // And flip if the cutting line was flipped around to make inside/outside labels match shape position
    if(!isKeepInside)
      lineExtendDir.negate()
    if(this.flipPlaneDirection)
      lineExtendDir.negate()

    this.editor.startUndoGroup('Cut Shape')
    this.editor.addUndoShape(this.selectedShape)

    // Cut shape by the plane
    try {

      this.selectedShape.cutShapeByPlane(lineExtendDir, this.lineDetails.start)
    } catch(err) {
      // Undo anything done by the failed cut shape
      this.editor.closeCurrentUndoGroup()
      this.editor.undoLastAction()

      this.editor.component.errorMessage('Error cutting shape', err.message)
      return
    }

    this.editor.closeCurrentUndoGroup()

    this.editor.onShapeUpdated(this.selectedShape)

    // Finish tool
    this.editor.cancelActiveTool()
  }


  /**
   * Apply a clip to the shape
   * Only clips of a piece of the shape where the editing line intersects
   * Flags an error if the clipping line does not intersect with the shape
   */
  applyClipToShape(isKeepInside) {
    let lineDir = new THREE.Vector2(this.lineDetails.end.x - this.lineDetails.start.x, this.lineDetails.end.y - this.lineDetails.start.y)
    lineDir.normalize()
    var lineExtendDir = new THREE.Vector2(-lineDir.y, lineDir.x)

    // Flip based on what side we keep/lose
    // And flip if the cutting line was flipped around to make inside/outside labels match shape position
    if(!isKeepInside)
      lineExtendDir.negate()
    if(this.flipPlaneDirection)
      lineExtendDir.negate()

    this.editor.startUndoGroup('Cut Shape')
    this.editor.addUndoShape(this.selectedShape)

    // Clip shape by a line
    try {

      this.selectedShape.clipShapeByLine(this.editingLine.startPoint, this.editingLine.endPoint, lineExtendDir)
    } catch(err) {
      // Undo anything done by the failed cut shape
      this.editor.closeCurrentUndoGroup()
      this.editor.undoLastAction()

      this.editor.component.errorMessage('Error clipping shape', err.message)
      return
    }

    this.editor.closeCurrentUndoGroup()

    this.editor.onShapeUpdated(this.selectedShape)

    // Finish tool
    this.editor.cancelActiveTool()
  }


  /**
   * Seperate the shape into 2 shapes by the cutting line
   */
  applySeperateToShape() {
    let lineDir = new THREE.Vector2(this.lineDetails.end.x - this.lineDetails.start.x, this.lineDetails.end.y - this.lineDetails.start.y)
    lineDir.normalize()
    var lineExtendDir = new THREE.Vector2(-lineDir.y, lineDir.x)

    // Ensure the cutting plane is angles so top/left is kept in 1st shape
    // and bottom/right is kept in 2nd shape
    if(lineExtendDir.dot(new THREE.Vector2(-1, -1)) < 0)
      lineExtendDir.negate()

    this.editor.startUndoGroup('Seperate Shape')
    this.editor.addUndoShape(this.selectedShape)

    try {
      // Clone the shape
      // Move it across to the next free space on the right of all the shapes
      var selectedShapeBounds = this.selectedShape.calculateSegmentBounds()
      var availablePosition = this.editor.getAvailableShapePosition(selectedShapeBounds)

      var newShape = this.selectedShape.clone()

      // Set the position of it
      let newShapeBounds = newShape.calculateSegmentBounds()
      newShape.moveShape(new THREE.Vector2(availablePosition.x - newShapeBounds.min.x, availablePosition.y - newShapeBounds.min.y))     
      newShape.segmentListUpdated()

      // Cut side of the original shape
      var cutLineOffset = this.lineDetails.start.clone().sub(selectedShapeBounds.min)
      this.selectedShape.cutShapeByPlane(lineExtendDir, this.lineDetails.start, { removeCenterCutouts: true })
      MathsUtils.roundSegmentListPositions(this.selectedShape.segmentList, 5)
      
      // Cut other side of the new shape
      lineExtendDir.negate()
      newShape.cutShapeByPlane(lineExtendDir, newShape.calculateSegmentBounds().min.add(cutLineOffset), { removeCenterCutouts: true })
      MathsUtils.roundSegmentListPositions(newShape.segmentList, 5)

      newShape.updateSceneObject()

      this.editor.onShapeUpdated(this.selectedShape)
      this.editor.onShapeUpdated(newShape)

      this.editor.addUndoShape(newShape, GlassDesignerTypes.UndoCommandTypes.add)
      this.editor.closeCurrentUndoGroup()

      // View all shapes
      this.editor.moveCameraToAllGlassShapes()


    } catch(err) {
      // Undo anything done by the failed cut shape
      this.editor.closeCurrentUndoGroup()
      this.editor.undoLastAction()

      this.editor.component.errorMessage('Error seperating shape', err.message)
      return
    }

    this.editor.closeCurrentUndoGroup()

    this.editor.onShapeUpdated(this.selectedShape)
    this.closeTool()
  }


  /**
   * Slice the shape by the cutting line, adding points where the shape edges intersect the cutting line
   */
  applySliceToShape() {
    this.editor.startUndoGroup('Slice Shape')
    this.editor.addUndoShape(this.selectedShape)

    // Slice the shape
    try {

      this.selectedShape.spliceLine(this.editingLine.startPoint, this.editingLine.endPoint)
    } catch(err) {
      // Undo anything done by the failed cut shape
      this.editor.closeCurrentUndoGroup()
      this.editor.undoLastAction()

      this.editor.component.errorMessage('Error slicing shape', err.message)
      return
    }

    this.editor.closeCurrentUndoGroup()

    this.editor.onShapeUpdated(this.selectedShape)
    this.closeTool()
  }


  /**
   * Editing line was updated
   */
  onEditingLineUpdated() {
    this.castStraightCutLine.startPosition.copy(this.lineDetails.maxExtentStart)
    this.castStraightCutLine.endPosition.copy(this.lineDetails.maxExtentEnd)
    this.castStraightCutLine.updateSceneObject()


    let lineDir = this.editingLine.getDirection()
    var lineExtendDir = new THREE.Vector3(-lineDir.y, lineDir.x)

    var editingPlane = new THREE.Plane()
    editingPlane.setFromNormalAndCoplanarPoint(lineExtendDir, this.editingLine.startPoint)

    // Figure out inside/outside the of the line facing the center of the shape
    let isLineFacingAway = editingPlane.distanceToPoint(new THREE.Vector3(this.selectedShapeCenter.x, this.selectedShapeCenter.y, 0)) > 0
    this.editingLine.swapDirectionLabels = !isLineFacingAway
    this.flipPlaneDirection = !isLineFacingAway
  }

}
